/**
 * The workspace's credit balance, in the header — one place, every page.
 *
 * The chip used to be pasted onto individual page heroes, which meant the
 * balance existed on /mmm and /insights and was simply absent everywhere else,
 * although credits belong to the workspace, not to a page. This mount replaces
 * those; what stays behind on the pages are the PRICE labels beside specific
 * actions ("ใช้ 1 เครดิต" on an export button), because a price belongs next to
 * the thing it prices and a balance does not.
 *
 * Renders nothing when credits are disabled or unloaded — the header must not
 * carry an empty pill for a feature that is off.
 *
 * @module components/HeaderCreditChip
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CreditChip } from "@/components/CreditChip";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { useIsPlatformMaster } from "@/hooks/useIsPlatformMaster";

export const HeaderCreditChip: React.FC = () => {
  const { t } = useTranslation();
  // The shared snapshot: this mount reads what the page chips already fetched,
  // so putting it in the header costs no extra query.
  const { balance, rate } = useCreditBalance();
  const { isMaster } = useIsPlatformMaster(true);
  const navigate = useNavigate();

  if (balance === null) return null;

  // "เหลือ N" is already compact enough for a phone header; no responsive
  // variant needed.
  const chip = <CreditChip balance={balance} rate={rate} />;

  // Masters land on the top-up screen; for everyone else it is a label, not a
  // control — a click that silently does nothing is the kind of inert button
  // this app has been removing.
  if (!isMaster) return chip;
  return (
    <button
      type="button"
      onClick={() => navigate("/admin/ai/credits")}
      className="rounded-full transition-opacity hover:opacity-70"
      title={t("credits.manage")}
    >
      {chip}
    </button>
  );
};

export default HeaderCreditChip;
